import { Breakpoints, Image, styled, Text, TokenTextAppearance, TokenTextColor } from '@volkswagen-onehub/components-core';
import React, { useContext } from 'react';
import { SelectedAcodesContext } from '../../../context/SelectedAcodes';
import { ResponseVehicle } from '../../../types/api';
import { TrackingEvents } from '../../../types/enums/tracking-events.enum';
import { useFeatureServices } from '../../../use-feature-services';

const ThumbnailButton = styled.button<{
  isSelected: boolean;
  widthDenominator: number;
}>`
  flex: 0 0 auto;
  width: calc((100vw - var(--size-grid004) - var(--size-dynamic0040))/2);
  padding: 8px 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  text-align: center;
  cursor: pointer;
  border-radius: ${({theme}) => theme.size.borderRadius.filterTag};
  border: none;

  /* Default (not selected) state */
  color: ${({theme}) => theme.colors.content.disabled};
  background: ${({theme}) => theme.colors.background.tertiary};
  box-shadow: 0 0 0 ${({theme}) => theme.size.border.active} ${({theme}) => theme.colors.border.disabled};

  /* Selected state */
  ${({isSelected, theme}) => isSelected && `
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.content.active};
    box-shadow: 0 0 0 ${theme.size.border.active} ${theme.colors.border.hover};
  `}

  /* Hover state */
  &:hover:not(:disabled) {
    transition: box-shadow 300ms ease, background 300ms ease;
    -webkit-transition: box-shadow 300ms ease, background 300ms ease;
    background: ${({theme}) => theme.colors.background.secondary};
    color: ${({theme}) => theme.colors.content.active};
    ${({isSelected, theme}) => !isSelected && `
      box-shadow: 0 0 0 ${theme.size.border.active} ${theme.colors.border.hover};
    `}

    ${({isSelected, theme}) => isSelected && `
      box-shadow: 0 0 0 ${theme.size.border.active} ${theme.colors.border.hover};
    `}

    #jelly-toggle {
      opacity: 1;
    }
    &:focus-visible {
      box-shadow: 0 0 0 ${({theme}) => theme.size.border.focus} ${({theme}) => theme.colors.focus.main};
    }
  }

  /* Transition on mouse leave only */
  &:not(:hover) {
    transition: box-shadow ${({theme}) => theme.animation.duration.layer} ease, background ${({theme}) => theme.animation.duration.layer} ease;
    -webkit-transition: box-shadow ${({theme}) => theme.animation.duration.layer} ease, background ${({theme}) => theme.animation.duration.layer} ease;
  }

  /* Focus state */
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({theme}) => theme.size.border.focus} ${({theme}) => theme.colors.focus.main};
    background: ${({theme}) => theme.colors.background.tertiary};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
    color: ${({theme}) => theme.colors.content.disabled};
    box-shadow: 0 0 0 ${({theme}) => theme.size.border.default} ${({theme}) => theme.colors.border.disabled};
  }

  @media (min-width: ${Breakpoints.b560}px) {
    width: calc((100vw - var(--size-grid004) - (var(--size-dynamic0040)*2))/3);
  }

  @media (min-width: 1025px) {
    width: calc(100%/${({widthDenominator}) => widthDenominator});
  }

  img {
    width: 60%;
  }

  span {
    text-decoration: none;
  }
`;

const StyledText = styled(Text)`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  color: inherit;
`;

interface Props {
  acode: string;
  model: string;
  src: string;
  isSelected: boolean;
  setAcode: (acode: string) => void;
  selectionLength: number;
}

export const VehicleThumb = ({acode, model, src, isSelected, setAcode, selectionLength}: Props) => {
  const { tracking } = useFeatureServices();
  const { acodes, data } = useContext(SelectedAcodesContext);

  const disabled = acodes.length === 1 && acodes.includes(acode);

  const handleClick = (acode: string) => {
    const vehicle = (data as ResponseVehicle[])?.filter(x => x.acode === acode)[0];

    tracking.track(TrackingEvents.MODELSELECT_CLICK, {
      eventInfo: {
        eventAction: TrackingEvents.MODELSELECT_CLICK,
        contentId: !isSelected ? "select vehicle" : "deselect vehicle"
      },
      environment: {
        featureAppId: 'CompareDimensionsWidget-D6-CA',
        featureAppIdAppVersion: '1.0',
      },
      personalization: {
        status: false,
      },
      configuration: {
        CarlineId: vehicle?.carline_id,
        CarlineName: vehicle.model,
        ModelId: vehicle?.sales_code,
        ModelName: vehicle.trim,
        ModelYear: vehicle.year.toString(),
      },
      link: {
        name: vehicle.model + " thumbnail selection",
        url: window.location.href
      }
    });
    setAcode(acode);
  };

  return (
    <ThumbnailButton isSelected={isSelected} widthDenominator={selectionLength+2} onClick={() => handleClick(acode)} disabled={disabled}>
      <Image src={src} aria-hidden role="presentation" alt="" />
      <StyledText appearance={TokenTextAppearance.label100} color={TokenTextColor.inherit}>{model}</StyledText>
    </ThumbnailButton>
  );
};
